import React, { useState, useEffect } from 'react';
import './App.css';
import LatestPrice from './components/LatestPrice';
import WeeklySummary from './components/WeeklySummary';
import MonthlySummary from './components/MonthlySummary';
import PriceRangeChart from './components/PriceRangeChart';
import ExportReport from './components/ExportReport';
import UploadPrice from './components/UploadPrice';
import BulkUploadPrice from './components/BulkUploadPrice';
import CandlestickChart from './components/CandlestickChart';
import UserProfile from './components/UserProfile';
import QsFeed from './components/QsFeed';
import WeeklyCommercialization from './components/WeeklyCommercialization';
import BMPApp from './components/marketplace/BMPApp';
import LandingPage from './components/LandingPage';
import AuthForm from './components/AuthForm';
import UserSearch from './components/UserSearch';
import NotificationsBell from './components/NotificationsBell';
import PublicUserProfile from './components/PublicUserProfile';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import axios from 'axios';
import AuthService from './services/AuthService';
import BannerSlider from './components/BannerSlider';

import logo from './LogoBanaprecios.png';

function App() {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState('latest');
  const [isLoggedIn, setIsLoggedIn] = useState(AuthService.isAuthenticated());
  const [userInfo, setUserInfo]     = useState(null);
  const [showAuth, setShowAuth]     = useState(false); // para Q's sin login
  const [showSearch, setShowSearch] = useState(false);
  const [authMode, setAuthMode]     = useState('login'); // 'login' | 'register'
  const [navOpen, setNavOpen]       = useState(false);
  const [viewingUser, setViewingUser]         = useState(null);
  const [profileRefreshKey, setProfileRefreshKey] = useState(0);
  const [prevView, setPrevView]               = useState('qs');

  useEffect(() => {
    if (isLoggedIn) {
      AuthService.setAuthHeader();

      const fetchUserInfo = async () => {
        const stillValid = await AuthService.refreshToken();
        if (!stillValid) {
          AuthService.logout();
          setIsLoggedIn(false);
          setUserInfo(null);
        } else {
          try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/user-info/`);
            setUserInfo(res.data);
          } catch (err) {
            console.error('Error fetching user info:', err);
          }
        }
      };

      fetchUserInfo();
    }
  }, [isLoggedIn]);

  const handleLogout = () => {
    AuthService.logout();
    setIsLoggedIn(false);
    setUserInfo(null);
    setActiveView('latest');
  };

  const handleAuthSuccess = () => {
    setIsLoggedIn(true);
    setShowAuth(false);
  };

  const navigate = (view) => { setActiveView(view); setNavOpen(false); };

  const onViewProfile = (username) => {
    if (username === userInfo?.username) {
      navigate('profile');
    } else {
      setPrevView(activeView);
      setViewingUser(username);
      navigate('public_profile');
    }
  };

  const onFollowChanged = () => setProfileRefreshKey(k => k + 1);

  // ── Landing / Auth gate ─────────────────────────────────────────
  if (!isLoggedIn && activeView !== 'qs' && !showAuth) {
    return (
      <LandingPage
        onLogin={() => { setAuthMode('login'); setShowAuth(true); }}
        onRegister={() => { setAuthMode('register'); setShowAuth(true); }}
      />
    );
  }

  if (showAuth && !isLoggedIn) {
    return (
      <div className="auth-banner-page">
        <div className="auth-banner-side">
          <BannerSlider position="auth_left" side="left" />
        </div>
        <AuthForm
          initialMode={authMode}
          onAuthSuccess={handleAuthSuccess}
          onShowQs={() => { setShowAuth(false); setActiveView('qs'); }}
        />
        <div className="auth-banner-side">
          <BannerSlider position="auth_right" side="right" />
        </div>
      </div>
    );
  }

  // ── Q's sin login ───────────────────────────────────────────────
  // Si el usuario no está logueado y quiere ver Q's, mostrar layout mínimo
  if (!isLoggedIn && activeView === 'qs') {
    return (
      <div className="App">
        <header className="App-header">
          <div className="header-container">
            <div className="brand-section">
              <div className="logo-container">
                <img src={logo} alt="BanaPrecios Logo" />
              </div>
              <div className="brand-text">
                <h1>{t('title')}</h1>
              </div>
            </div>
            <div className="header-actions">
              <button
                className="btn btn-success"
                style={{ fontSize: '0.82rem', padding: '8px 16px' }}
                onClick={() => setShowAuth(true) || setActiveView('latest')}
              >
                {t('login_register')}
              </button>
            </div>
          </div>
        </header>
        <main className="App-main">
          <div className="banner-layout">
            <QsFeed isLoggedIn={false} currentUser={null} onViewProfile={onViewProfile} />
            <BannerSlider position="feed_right" side="right" />
          </div>
        </main>
      </div>
    );
  }

  // ── BMP: layout takeover ─────────────────────────────────────
  if (activeView === 'marketplace') {
    return (
      <BMPApp
        userInfo={userInfo}
        onExitMarketplace={() => navigate('latest')}
        onLogout={handleLogout}
      />
    );
  }

  const renderActiveView = () => {
    const restrictedViews = ['weekly', 'monthly', 'range', 'export', 'upload', 'bulk', 'live'];

    if (activeView === 'commercialization' && !userInfo?.can_commercialize) {
      return (
        <div className="access-denied">
          <p>
            {t('access_denied_commercialization')}<br />
            {t('access_denied_message_contact')}{' '}
            <span
              className="green-email"
              onClick={() => { navigator.clipboard.writeText('info.banaprecios@gmail.com'); alert(t('email_copied')); }}
              title={t('click_to_copy')}
            >
              info.banaprecios@gmail.com
            </span>{' '}
            {t('contact_to_activate')}
          </p>
        </div>
      );
    }

    if (restrictedViews.includes(activeView) && !userInfo?.is_subscribed) {
      return (
        <div className="access-denied">
          <p>
            {t('access_denied_message_prefix')}<br />
            {t('access_denied_message_contact')}{' '}
            <span
              className="green-email"
              onClick={() => {
                navigator.clipboard.writeText('info.banaprecios@gmail.com');
                alert(t('email_copied'));
              }}
              title={t('click_to_copy')}
            >
              info.banaprecios@gmail.com
            </span>{' '}
            {t('access_denied_message_suffix')}
          </p>
        </div>
      );
    }

    switch (activeView) {
      case 'latest':  return (
        <div className="banner-layout">
          <LatestPrice />
          <BannerSlider position="price_right" side="right" />
        </div>
      );
      case 'weekly':  return <WeeklySummary />;
      case 'monthly': return <MonthlySummary />;
      case 'range':   return <PriceRangeChart />;
      case 'export':  return <ExportReport />;
      case 'upload':  return <UploadPrice />;
      case 'bulk':    return <BulkUploadPrice />;
      case 'live':    return <CandlestickChart />;
      case 'qs':      return (
        <div className="banner-layout">
          <QsFeed isLoggedIn={isLoggedIn} currentUser={userInfo} onViewProfile={onViewProfile} />
          <BannerSlider position="feed_right" side="right" />
        </div>
      );
      case 'profile': return (
        <UserProfile
          currentUser={userInfo}
          refreshKey={profileRefreshKey}
          onProfileUpdated={(updated) => setUserInfo(prev => ({ ...prev, ...updated }))}
          onViewProfile={onViewProfile}
        />
      );
      case 'public_profile': return (
        <PublicUserProfile
          username={viewingUser}
          currentUser={userInfo}
          onBack={() => navigate(prevView)}
          onFollowChanged={onFollowChanged}
          onViewProfile={onViewProfile}
        />
      );
      case 'commercialization': return <WeeklyCommercialization />;
      case 'marketplace': return null; // handled by early return above
      default: return <LatestPrice />;
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-container">
          <div className="brand-section">
            <div className="logo-container">
              <img src={logo} alt="BanaPrecios Logo" />
            </div>
            <div className="brand-text">
              <h1>{t('title')}</h1>
              {userInfo && <p className="user-welcome">{t('welcome_user', { username: userInfo.username })}</p>}
            </div>
          </div>

          <div className="header-actions">
            <div className="language-switcher">
              <button type="button" onClick={() => i18n.changeLanguage('es')} title="Español">🇪🇨</button>
              <button type="button" onClick={() => i18n.changeLanguage('en')} title="English">🇺🇸</button>
            </div>
            {/* Buscar usuarios */}
            <button
              className="header-icon-btn"
              onClick={() => setShowSearch(true)}
              title={t('search_users_title')}
            >🔍</button>
            {/* Notificaciones */}
            <NotificationsBell onFollowChanged={onFollowChanged} />
            {/* Avatar / perfil en header */}
            <button
              className="header-avatar-btn"
              onClick={() => navigate('profile')}
              title={t('my_profile')}
            >
              {userInfo?.photo_url
                ? <img src={userInfo.photo_url} alt="perfil" className="header-avatar-img" />
                : <span className="header-avatar-initials">
                    {(userInfo?.username || '?')[0].toUpperCase()}
                  </span>
              }
            </button>
            {/* Hamburger — visible solo en móvil */}
            <button
              className="hamburger-btn"
              onClick={() => setNavOpen(prev => !prev)}
              aria-label={t('menu')}
            >
              {navOpen ? '✕' : '☰'}
            </button>
          </div>
          {/* Modal buscador */}
          {showSearch && <UserSearch onClose={() => setShowSearch(false)} onFollowChanged={onFollowChanged} onViewProfile={onViewProfile} />}
        </div>
      </header>

      <nav className={`main-nav-section${navOpen ? ' nav--open' : ''}`}>
        <div className="nav-container">
          <ul className="main-nav-list">
            <li><button className={activeView === 'latest'  ? 'active' : ''} onClick={() => navigate('latest')}>{t('latest_price')}</button></li>
            <li><button className={activeView === 'weekly'  ? 'active' : ''} onClick={() => navigate('weekly')}>{t('weekly_summary')}</button></li>
            <li><button className={activeView === 'monthly' ? 'active' : ''} onClick={() => navigate('monthly')}>{t('monthly_summary')}</button></li>
            <li><button className={activeView === 'range'   ? 'active' : ''} onClick={() => navigate('range')}>{t('price_range')}</button></li>
            <li><button className={activeView === 'live'    ? 'active' : ''} onClick={() => navigate('live')}>{t('live_market')}</button></li>
            <li><button className={activeView === 'qs'      ? 'active' : ''} onClick={() => navigate('qs')}>Q's</button></li>
            <li><button className={activeView === 'export'  ? 'active' : ''} onClick={() => navigate('export')}>{t('export_reports')}</button></li>
            {userInfo?.can_upload_prices && (
              <li><button className={activeView === 'upload' ? 'active' : ''} onClick={() => navigate('upload')}>{t('upload_price')}</button></li>
            )}
            {userInfo?.can_upload_prices && (
              <li><button className={activeView === 'bulk' ? 'active' : ''} onClick={() => navigate('bulk')}>{t('bulk_upload_nav')}</button></li>
            )}
            {userInfo?.can_commercialize && (
              <li><button className={activeView === 'commercialization' ? 'active' : ''} onClick={() => navigate('commercialization')}>{t('commercialization')}</button></li>
            )}
            {userInfo?.is_staff && (
              <li><button className={activeView === 'marketplace' ? 'active' : ''} onClick={() => navigate('marketplace')}>{t('marketplace')}</button></li>
            )}
            <li><button onClick={() => { handleLogout(); setNavOpen(false); }}>{t('logout')}</button></li>
          </ul>
        </div>
      </nav>

      <main className="App-main">
        {renderActiveView()}
      </main>

      <footer className="App-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>BanaPrecios</h4>
            <p>{t('footer_brand_desc')}</p>
            <p>{t('footer_brand_desc2')}</p>
          </div>
          <div className="footer-section">
            <h4>{t('footer_contact')}</h4>
            <p>
              <span className="green-email" onClick={() => {
                navigator.clipboard.writeText('info.banaprecios@gmail.com');
                alert(t('email_copied'));
              }}>info.banaprecios@gmail.com</span>
            </p>
            <p>{t('footer_contact_desc')}</p>
          </div>
          <div className="footer-section">
            <h4>{t('footer_sector')}</h4>
            <p>{t('footer_sector_desc1')}</p>
            <p>{t('footer_sector_desc2')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
